/**
 * Composer at the bottom of the chat panel.
 *
 * R-143-CHAT-ASSISTANT Phase D.
 */
import React, { useCallback, useState } from 'react';
import { Send, Loader2 } from 'lucide-react';

interface Props {
  value: string;
  onChange: (text: string) => void;
  onSubmit: (text: string) => Promise<void> | void;
  isWaiting?: boolean;
  placeholder?: string;
}

export function ChatInput({
  value,
  onChange,
  onSubmit,
  isWaiting,
  placeholder = 'Ask about this site, your portfolio, or any engineering question…',
}: Props): React.JSX.Element {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed || submitting || isWaiting) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
    } finally {
      setSubmitting(false);
    }
  }, [value, submitting, isWaiting, onSubmit]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter, allow Shift+Enter for newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit(e as unknown as React.FormEvent);
    }
  }, [handleSubmit]);

  const busy = submitting || isWaiting;
  return (
    <form
      onSubmit={handleSubmit}
      className="border-t-2 border-border bg-background p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] space-y-2"
      data-testid="chat-input-form"
    >
      <textarea
        rows={2}
        value={value}
        disabled={busy}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        aria-label="Chat message"
        className="w-full px-3 py-2 text-sm border-2 border-border bg-background text-text-primary focus:outline-none focus:border-primary rounded-md resize-none disabled:opacity-50"
        data-testid="chat-input-textarea"
      />
      <div className="flex items-center justify-between">
        <span className="text-[8px] uppercase tracking-widest text-text-muted">
          {busy ? 'Thinking…' : 'Enter to send · Shift+Enter for newline'}
        </span>
        <button
          type="submit"
          disabled={busy || !value.trim()}
          className="inline-flex items-center gap-1 px-3 py-1.5 border-2 border-primary bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 text-[10px] uppercase tracking-widest font-bold rounded-md"
          data-testid="chat-input-send"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          Send
        </button>
      </div>
    </form>
  );
}
